import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

import { Subject, takeUntil } from 'rxjs';

import { TreeNode, TreeviewCheckedSelection } from '@mirage-projects/cw-models';

import { TreeviewActionService } from './treeview-action/treeview-action.service';
import { TreeviewService } from './treeview.service';
/**
 * Treeview component
 */
@Component({
    selector: 'cw-treeview',
    templateUrl: 'treeview.component.html',
    styleUrls: ['./treeview.component.scss'],
    providers: [TreeviewActionService],
    standalone: false,
})
export class TreeviewComponent implements OnChanges, OnInit, OnDestroy {
    /** Tree definition data object */
    @Input() data!: TreeNode;
    /** The selected node */
    @Input() selectedNode: TreeNode | undefined | null;
    /** Enables multiple node selection */
    @Input() multiSelection = true;
    /** Enables/disables selection */
    @Input() enableSelection = true;
    /** Sets the treeview read only */
    @Input() isReadonly = false;
    /** Treeview icon size */
    @Input() iconSize = 29;

    /** Event emitter for the selected node */
    @Output() selectedNodeChange = new EventEmitter<TreeNode>();
    /** Event emitter when user clicks a treeview element checkbox. It emits the checked node and the checkbox boolean state */
    @Output() nodeCheckedChange = new EventEmitter<TreeviewCheckedSelection>();
    /** Event emitter when user changes the name*/
    @Output() nodeNameChange = new EventEmitter<TreeNode>();

    private onDestroy$: Subject<void> = new Subject();

    constructor(
        private treeviewService: TreeviewService,
        private treeviewActionService: TreeviewActionService
    ) {}

    ngOnInit(): void {
        this.treeviewActionService.nodeRename$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((treeNode: TreeNode) => this.nodeNameChange.emit(treeNode));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.selectedNode && this.data) {
            this.treeviewService.clearSelection(this.data);
            return;
        }

        if (this.selectedNode && changes['selectedNode']) {
            this.selectItem(this.selectedNode);
        }
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    onItemCheckedChange(e: TreeviewCheckedSelection): void {
        this.treeviewService.updateChildCheckedState(e.node, e.checked);
        this.nodeCheckedChange.emit(e);
    }

    /**
     * Changes the selection state of the node
     * @param node Node to change the selection state
     * @param hasToEmitChange Tells if the component emit the change or not
     */
    selectItem(node: TreeNode, hasToEmitChange?: boolean): void {
        if (!this.enableSelection) {
            return;
        }
        this.updateSelection(this.data, node);
        this.selectedNode = node;

        if (hasToEmitChange) {
            this.selectedNodeChange.emit(node);
        }
    }

    /**
     * Applys the search text on the treeview nodes and emits the text to be selected to the node views
     * @param text Text to be searched
     */
    applySearchText(text: string): void {
        this.treeviewActionService.applySearchText(this.data, text);
    }

    private updateSelection(treeNode: TreeNode, selectedNode: TreeNode): void {
        treeNode.selected = treeNode === selectedNode || treeNode.id === selectedNode.id;

        treeNode?.children?.forEach((child) => {
            this.updateSelection(child, selectedNode);
        });
    }
}
